import type { QueryParams } from '@/lib/api/client';

/**
 * Centralized query-key factory (plan §4.4). Every key is [domain, scope, ...params] so
 * mutations can invalidate precise families. Lists embed their params for cache separation.
 */
export const keys = {
  me: () => ['me'] as const,
  dashboard: () => ['dashboard'] as const,
  users: {
    all: () => ['users'] as const,
    list: (p?: QueryParams) => ['users', 'list', p ?? {}] as const,
  },
  apiTokens: {
    all: () => ['api-tokens'] as const,
    list: (p?: QueryParams) => ['api-tokens', 'list', p ?? {}] as const,
    options: () => ['api-tokens', 'options'] as const,
  },
  blocks: { schemas: () => ['blocks', 'schemas'] as const },
  collectionSchemas: () => ['collection-schemas'] as const,
  pages: {
    all: () => ['pages'] as const,
    list: (p?: QueryParams) => ['pages', 'list', p ?? {}] as const,
    detail: (id: string) => ['pages', 'detail', id] as const,
    sections: (id: string) => ['pages', 'sections', id] as const,
  },
  families: {
    all: () => ['families'] as const,
    list: (p?: QueryParams) => ['families', 'list', p ?? {}] as const,
    detail: (id: string) => ['families', 'detail', id] as const,
  },
  content: {
    all: (collection: string) => ['content', collection] as const,
    list: (collection: string, p?: QueryParams) => ['content', collection, 'list', p ?? {}] as const,
    detail: (collection: string, id: string) => ['content', collection, 'detail', id] as const,
  },
  media: {
    all: () => ['media'] as const,
    list: (p?: QueryParams) => ['media', 'list', p ?? {}] as const,
    detail: (id: string) => ['media', 'detail', id] as const,
    usages: (id: string) => ['media', 'usages', id] as const,
  },
  leads: {
    all: (type: string) => ['leads', type] as const,
    lists: (type: string) => ['leads', type, 'list'] as const,
    list: (type: string, p?: QueryParams) => ['leads', type, 'list', p ?? {}] as const,
    detail: (type: string, id: string) => ['leads', type, 'detail', id] as const,
  },
  revisions: (entityType: string, entityId: string) =>
    ['revisions', entityType, entityId] as const,
  audit: (p?: QueryParams) => ['audit', p ?? {}] as const,
  settings: {
    doc: () => ['settings', 'doc'] as const,
    strings: () => ['settings', 'strings'] as const,
    navigation: (key: string) => ['settings', 'navigation', key] as const,
    redirects: (p?: QueryParams) => ['settings', 'redirects', p ?? {}] as const,
    contactDirectory: () => ['settings', 'contact-directory'] as const,
    countries: () => ['settings', 'countries'] as const,
    pageCopy: () => ['settings', 'page-copy'] as const,
    pageCopyDetail: (ns: string) => ['settings', 'page-copy', ns] as const,
    pageCopyUsages: (ns: string) => ['settings', 'page-copy', ns, 'usages'] as const,
  },
  search: (q: string, locale: string) => ['search', q, locale] as const,
} as const;
